// WorkflowForm.js - Create/Edit CRM Contact Workflow with Step Builder

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Paper, IconButton, Chip, Alert, CircularProgress, Stack,
  Grid, FormControlLabel, Switch, Divider, Card, CardContent, CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Rule as RuleIcon,
  ThumbUp as ThumbUpIcon,
  ContentCopy as TemplateIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { workflowService, bookingPageService } from './activityCommService';
import {
  WorkflowTypeOptions,
  WorkflowTriggerTypeOptions,
  WorkflowStepTypeOptions,
  WorkflowStepTypeLabels
} from './activityCommEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STEP_ICON_MAP = {
  SEND_EMAIL: <EmailIcon />,
  SEND_SMS: <SmsIcon />,
  CREATE_TASK: <AssignmentIcon />,
  SCHEDULE_APPOINTMENT: <EventIcon />,
  SEND_NOTIFICATION: <NotificationsIcon />,
  WAIT_DELAY: <HourglassEmptyIcon />,
  CHECK_CONDITION: <RuleIcon />,
  REQUIRE_APPROVAL: <ThumbUpIcon />
};

const VARIABLE_HINT = 'Variables: {{contact.first_name}}, {{contact.last_name}}, {{contact.email}}, {{contact.balance_due}}';

const CONDITION_FIELDS = [
  { value: 'contact.balance_due', label: 'Balance Due' },
  { value: 'contact.status', label: 'Contact Status' }
];

const CONDITION_OPERATORS = [
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal' },
  { value: 'eq', label: 'Equal to' },
  { value: 'neq', label: 'Not equal to' }
];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' }
];

const createEmptyStepConfig = (stepType) => {
  switch (stepType) {
    case 'SEND_EMAIL':
      return { subject: '', body: '' };
    case 'SEND_SMS':
      return { message: '' };
    case 'CREATE_TASK':
      return { title: '', description: '', priority: 'MEDIUM', due_in_days: 1 };
    case 'SCHEDULE_APPOINTMENT':
      return { booking_page_id: '', email_subject: '', email_body: '' };
    case 'SEND_NOTIFICATION':
      return { title: '', message: '' };
    case 'WAIT_DELAY':
      return { delay_days: 0, delay_hours: 0 };
    case 'CHECK_CONDITION':
      return { field: 'contact.balance_due', operator: 'gt', value: '' };
    case 'REQUIRE_APPROVAL':
      return { message: '' };
    default:
      return {};
  }
};

const WORKFLOW_TEMPLATES = [
  {
    id: 'appointment_reminder',
    label: 'Appointment Reminder',
    description: 'Remind contacts to schedule their next appointment after service completion.',
    icon: <EventIcon sx={{ fontSize: 36, color: '#2e7d32' }} />,
    color: '#2e7d32',
    form: {
      name: 'Appointment Reminder',
      description: 'Automatically remind contacts to schedule their next appointment after their last service.',
      workflow_type: 'SERVICE_REMINDER',
      trigger_type: 'AUTO_AFTER_SERVICE',
      trigger_config: { days_after_last_service: 30 },
      is_active: true
    },
    steps: [
      {
        step_type: 'SEND_EMAIL',
        name: 'Reminder Email',
        config: {
          subject: 'Time to Schedule Your Next Appointment, {{contact.first_name}}!',
          body: '<p>Hi {{contact.first_name}},</p><p>It has been a while since your last visit. We recommend scheduling your next appointment to stay on track.</p><p>Click the link below to book a convenient time:</p><p>We look forward to seeing you!</p>'
        }
      },
      {
        step_type: 'SCHEDULE_APPOINTMENT',
        name: 'Send Booking Link',
        config: { booking_page_id: '', email_subject: 'Book Your Appointment', email_body: '<p>Hi {{contact.first_name}},</p><p>Please use the link below to schedule your next appointment:</p><p><a href="{{booking_page_url}}">{{booking_page_title}}</a></p>' }
      },
      {
        step_type: 'WAIT_DELAY',
        name: 'Wait 7 Days',
        config: { delay_days: 7, delay_hours: 0 }
      },
      {
        step_type: 'SEND_SMS',
        name: 'Follow-up SMS',
        config: { message: 'Hi {{contact.first_name}}, just a reminder to book your next appointment. Don\'t miss out on staying on schedule!' }
      },
      {
        step_type: 'SEND_NOTIFICATION',
        name: 'Notify Agent',
        config: { title: 'Appointment Not Booked', message: '{{contact.first_name}} {{contact.last_name}} has not booked their appointment after 7 days.' }
      }
    ]
  },
  {
    id: 'payment_overdue',
    label: 'Payment Overdue Reminder',
    description: 'Send reminders to contacts with overdue payments until balance is cleared.',
    icon: <RuleIcon sx={{ fontSize: 36, color: '#d32f2f' }} />,
    color: '#d32f2f',
    form: {
      name: 'Payment Overdue Reminder',
      description: 'Automatically follow up with contacts who have overdue invoices. Checks balance and sends reminders.',
      workflow_type: 'PAYMENT_FOLLOWUP',
      trigger_type: 'AUTO_OVERDUE_PAYMENT',
      trigger_config: { overdue_days: 7 },
      is_active: true
    },
    steps: [
      {
        step_type: 'CHECK_CONDITION',
        name: 'Check Balance Due',
        config: { field: 'contact.balance_due', operator: 'gt', value: '0' }
      },
      {
        step_type: 'SEND_EMAIL',
        name: 'Payment Reminder Email',
        config: {
          subject: 'Payment Reminder - Balance Due: {{contact.balance_due}}',
          body: '<p>Hi {{contact.first_name}},</p><p>This is a friendly reminder that you have an outstanding balance of <strong>{{contact.balance_due}}</strong>.</p><p>Please make your payment at your earliest convenience to avoid any service interruptions.</p><p>If you have already made this payment, please disregard this message.</p><p>Thank you!</p>'
        }
      },
      {
        step_type: 'WAIT_DELAY',
        name: 'Wait 3 Days',
        config: { delay_days: 3, delay_hours: 0 }
      },
      {
        step_type: 'SEND_SMS',
        name: 'Payment Reminder SMS',
        config: { message: 'Hi {{contact.first_name}}, you have an outstanding balance of {{contact.balance_due}}. Please make your payment soon. Thank you!' }
      },
      {
        step_type: 'WAIT_DELAY',
        name: 'Wait 4 Days',
        config: { delay_days: 4, delay_hours: 0 }
      },
      {
        step_type: 'CREATE_TASK',
        name: 'Create Follow-up Task',
        config: { title: 'Follow up on overdue payment - {{contact.first_name}} {{contact.last_name}}', description: 'Contact has outstanding balance of {{contact.balance_due}}. Please follow up directly.', priority: 'HIGH', due_in_days: 2 }
      },
      {
        step_type: 'SEND_NOTIFICATION',
        name: 'Notify Agent',
        config: { title: 'Overdue Payment Alert', message: '{{contact.first_name}} {{contact.last_name}} still has an outstanding balance of {{contact.balance_due}} after multiple reminders.' }
      }
    ]
  },
  {
    id: 'post_purchase_followup',
    label: 'Post-Purchase Follow-up',
    description: 'Thank contacts after a purchase and follow up for feedback or next steps.',
    icon: <EmailIcon sx={{ fontSize: 36, color: '#1976d2' }} />,
    color: '#1976d2',
    form: {
      name: 'Post-Purchase Follow-up',
      description: 'Automatically send a thank-you message after a purchase and follow up for feedback.',
      workflow_type: 'GENERAL_COMMUNICATION',
      trigger_type: 'AUTO_AFTER_PURCHASE',
      trigger_config: { days_after_purchase: 1 },
      is_active: true
    },
    steps: [
      {
        step_type: 'SEND_EMAIL',
        name: 'Thank You Email',
        config: {
          subject: 'Thank You for Your Purchase, {{contact.first_name}}!',
          body: '<p>Hi {{contact.first_name}},</p><p>Thank you for your recent purchase! We truly appreciate your business.</p><p>If you have any questions or need assistance, feel free to reach out to us anytime.</p><p>We look forward to serving you again!</p>'
        }
      },
      {
        step_type: 'WAIT_DELAY',
        name: 'Wait 5 Days',
        config: { delay_days: 5, delay_hours: 0 }
      },
      {
        step_type: 'SEND_EMAIL',
        name: 'Feedback Request Email',
        config: {
          subject: 'How Was Your Experience, {{contact.first_name}}?',
          body: '<p>Hi {{contact.first_name}},</p><p>We hope you are enjoying your recent purchase. We would love to hear your feedback!</p><p>Your opinion helps us improve and serve you better.</p><p>Thank you for taking the time!</p>'
        }
      },
      {
        step_type: 'SEND_NOTIFICATION',
        name: 'Notify Agent',
        config: { title: 'Post-Purchase Follow-up Sent', message: 'Follow-up emails sent to {{contact.first_name}} {{contact.last_name}}.' }
      }
    ]
  },
  {
    id: 'event_notification',
    label: 'Event / Announcement',
    description: 'Notify contacts about an upcoming event or announcement via email and SMS.',
    icon: <NotificationsIcon sx={{ fontSize: 36, color: '#ed6c02' }} />,
    color: '#ed6c02',
    form: {
      name: 'Event Notification',
      description: 'Send event notification or announcement to selected contacts via email and SMS.',
      workflow_type: 'GENERAL_COMMUNICATION',
      trigger_type: 'MANUAL',
      trigger_config: {},
      is_active: true
    },
    steps: [
      {
        step_type: 'SEND_EMAIL',
        name: 'Event Announcement Email',
        config: {
          subject: 'You\'re Invited! Upcoming Event',
          body: '<p>Hi {{contact.first_name}},</p><p>We are excited to invite you to our upcoming event!</p><p><strong>Event Details:</strong></p><p>[Add your event details here]</p><p>We hope to see you there!</p>'
        }
      },
      {
        step_type: 'SEND_SMS',
        name: 'Event Reminder SMS',
        config: { message: 'Hi {{contact.first_name}}, you are invited to our upcoming event! Check your email for full details.' }
      }
    ]
  },
  {
    id: 'new_client_onboarding',
    label: 'New Client Onboarding',
    description: 'Welcome new contacts with an onboarding sequence including email, booking, and follow-up.',
    icon: <AssignmentIcon sx={{ fontSize: 36, color: '#9c27b0' }} />,
    color: '#9c27b0',
    form: {
      name: 'New Client Onboarding',
      description: 'Welcome new clients with an onboarding email sequence, appointment scheduling, and agent follow-up.',
      workflow_type: 'CUSTOM',
      trigger_type: 'MANUAL',
      trigger_config: {},
      is_active: true
    },
    steps: [
      {
        step_type: 'SEND_EMAIL',
        name: 'Welcome Email',
        config: {
          subject: 'Welcome to Our Team, {{contact.first_name}}!',
          body: '<p>Hi {{contact.first_name}},</p><p>Welcome aboard! We are thrilled to have you as a new client.</p><p>Here is what you can expect next:</p><ul><li>A quick introduction call to understand your needs</li><li>Setup and onboarding assistance</li><li>Ongoing support from our dedicated team</li></ul><p>We will be in touch soon to schedule your first appointment!</p>'
        }
      },
      {
        step_type: 'SCHEDULE_APPOINTMENT',
        name: 'Schedule Intro Call',
        config: { booking_page_id: '', email_subject: 'Schedule Your Introduction Call', email_body: '<p>Hi {{contact.first_name}},</p><p>Please use the link below to schedule your introduction call at a time that works best for you:</p><p><a href="{{booking_page_url}}">{{booking_page_title}}</a></p>' }
      },
      {
        step_type: 'WAIT_DELAY',
        name: 'Wait 3 Days',
        config: { delay_days: 3, delay_hours: 0 }
      },
      {
        step_type: 'SEND_SMS',
        name: 'Follow-up SMS',
        config: { message: 'Hi {{contact.first_name}}, just checking in! Have you had a chance to schedule your introduction call? We are here to help!' }
      },
      {
        step_type: 'CREATE_TASK',
        name: 'Agent Follow-up Task',
        config: { title: 'Follow up with new client - {{contact.first_name}} {{contact.last_name}}', description: 'New client onboarding. Check if they have scheduled their intro call and assist if needed.', priority: 'MEDIUM', due_in_days: 5 }
      }
    ]
  }
];

const WorkflowForm = () => {
  const { chatbotId, workflowId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!workflowId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bookingPages, setBookingPages] = useState([]);
  const [addStepType, setAddStepType] = useState('');
  const [showTemplates, setShowTemplates] = useState(!isEditing);

  const [form, setForm] = useState({
    name: '',
    description: '',
    workflow_type: 'CUSTOM',
    trigger_type: 'MANUAL',
    trigger_config: {},
    is_active: true
  });

  const [steps, setSteps] = useState([]);

  useEffect(() => {
    document.title = isEditing
      ? 'Edit Workflow | Activity & Communications'
      : 'Create Workflow | Activity & Communications';
    loadBookingPages();
    if (isEditing) {
      loadWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const loadBookingPages = async () => {
    try {
      const res = await bookingPageService.getAll();
      setBookingPages(res.data || []);
    } catch (err) {
      console.error('Failed to load booking pages:', err);
    }
  };

  const loadWorkflow = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getById(workflowId);
      const wf = res.data || res;

      setForm({
        name: wf.name || '',
        description: wf.description || '',
        workflow_type: wf.workflow_type || 'CUSTOM',
        trigger_type: wf.trigger_type || 'MANUAL',
        trigger_config: wf.trigger_config || {},
        is_active: wf.is_active !== undefined ? wf.is_active : true
      });

      if (wf.steps && Array.isArray(wf.steps)) {
        setSteps(wf.steps.map((s, idx) => ({
          id: s.id || `step_${idx}`,
          step_type: s.step_type || 'SEND_EMAIL',
          name: s.name || '',
          step_order: s.step_order || idx + 1,
          config: s.config || createEmptyStepConfig(s.step_type)
        })));
      }
    } catch (err) {
      setError('Failed to load workflow');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTriggerConfigChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      trigger_config: { ...prev.trigger_config, [field]: value }
    }));
  };

  const handleStepConfigChange = (index, field, value) => {
    setSteps(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        config: { ...updated[index].config, [field]: value }
      };
      return updated;
    });
  };

  const handleStepNameChange = (index, value) => {
    setSteps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name: value };
      return updated;
    });
  };

  const handleAddStep = () => {
    if (!addStepType) return;
    setSteps(prev => [
      ...prev,
      {
        id: `new_${Date.now()}`,
        step_type: addStepType,
        name: WorkflowStepTypeLabels[addStepType] || addStepType,
        step_order: prev.length + 1,
        config: createEmptyStepConfig(addStepType)
      }
    ]);
    setAddStepType('');
  };

  const handleRemoveStep = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_order: i + 1 })));
  };

  const handleMoveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    setSteps(prev => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[newIndex];
      updated[newIndex] = temp;
      return updated.map((s, i) => ({ ...s, step_order: i + 1 }));
    });
  };

  const applyTemplate = (template) => {
    setForm({ ...template.form });
    setSteps(template.steps.map((s, idx) => ({
      id: `tpl_${Date.now()}_${idx}`,
      step_type: s.step_type,
      name: s.name,
      step_order: idx + 1,
      config: { ...s.config }
    })));
    setShowTemplates(false);
    enqueueSnackbar(`Template "${template.label}" applied. You can customize it before saving.`, { variant: 'info' });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Workflow name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: form.name,
        description: form.description,
        workflow_type: form.workflow_type,
        trigger_type: form.trigger_type,
        trigger_config: form.trigger_config,
        is_active: form.is_active,
        steps: steps.map((s, idx) => ({
          step_type: s.step_type,
          name: s.name,
          step_order: idx + 1,
          config: s.config
        }))
      };

      if (isEditing) {
        await workflowService.update(workflowId, payload);
        enqueueSnackbar('Workflow updated successfully', { variant: 'success' });
      } else {
        await workflowService.create(payload);
        enqueueSnackbar('Workflow created successfully', { variant: 'success' });
      }

      navigate(`/chatbot/${chatbotId}/crm/activity/workflows`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save workflow');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const renderTriggerConfig = () => {
    const { trigger_type, trigger_config } = form;

    if (trigger_type === 'AUTO_AFTER_SERVICE') {
      return (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily }}>
            Trigger Configuration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Days after last service"
                type="number"
                fullWidth
                value={trigger_config.days_after || ''}
                onChange={(e) => handleTriggerConfigChange('days_after', parseInt(e.target.value, 10) || '')}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Type ID (optional)"
                type="number"
                fullWidth
                value={trigger_config.service_type_id || ''}
                onChange={(e) => handleTriggerConfigChange('service_type_id', parseInt(e.target.value, 10) || '')}
              />
            </Grid>
          </Grid>
        </Paper>
      );
    }

    if (trigger_type === 'AUTO_OVERDUE_PAYMENT') {
      return (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily }}>
            Trigger Configuration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Days overdue"
            type="number"
            fullWidth
            value={trigger_config.days_overdue || ''}
            onChange={(e) => handleTriggerConfigChange('days_overdue', parseInt(e.target.value, 10) || '')}
            inputProps={{ min: 1 }}
          />
        </Paper>
      );
    }

    if (trigger_type === 'AUTO_AFTER_PURCHASE') {
      return (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily }}>
            Trigger Configuration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Days after purchase"
            type="number"
            fullWidth
            value={trigger_config.days_after_purchase || ''}
            onChange={(e) => handleTriggerConfigChange('days_after_purchase', parseInt(e.target.value, 10) || '')}
            inputProps={{ min: 1 }}
          />
        </Paper>
      );
    }

    return null;
  };

  const renderStepConfig = (step, index) => {
    const { step_type, config } = step;

    switch (step_type) {
      case 'SEND_EMAIL':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Subject"
              fullWidth
              value={config.subject || ''}
              onChange={(e) => handleStepConfigChange(index, 'subject', e.target.value)}
            />
            <TextField
              label="Body"
              fullWidth
              multiline
              rows={4}
              value={config.body || ''}
              onChange={(e) => handleStepConfigChange(index, 'body', e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">{VARIABLE_HINT}</Typography>
          </Stack>
        );

      case 'SEND_SMS':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={3}
              value={config.message || ''}
              onChange={(e) => handleStepConfigChange(index, 'message', e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">{VARIABLE_HINT}</Typography>
          </Stack>
        );

      case 'CREATE_TASK':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={config.title || ''}
              onChange={(e) => handleStepConfigChange(index, 'title', e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={config.description || ''}
              onChange={(e) => handleStepConfigChange(index, 'description', e.target.value)}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={config.priority || 'MEDIUM'}
                    label="Priority"
                    onChange={(e) => handleStepConfigChange(index, 'priority', e.target.value)}
                  >
                    {PRIORITY_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Due in days"
                  type="number"
                  fullWidth
                  value={config.due_in_days || ''}
                  onChange={(e) => handleStepConfigChange(index, 'due_in_days', parseInt(e.target.value, 10) || '')}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Stack>
        );

      case 'SCHEDULE_APPOINTMENT':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Booking Page</InputLabel>
              <Select
                value={config.booking_page_id || ''}
                label="Booking Page"
                onChange={(e) => handleStepConfigChange(index, 'booking_page_id', e.target.value)}
              >
                {bookingPages.map(bp => (
                  <MenuItem key={bp.id} value={bp.id}>{bp.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Email Subject"
              fullWidth
              value={config.email_subject || ''}
              onChange={(e) => handleStepConfigChange(index, 'email_subject', e.target.value)}
            />
            <TextField
              label="Email Body"
              fullWidth
              multiline
              rows={3}
              value={config.email_body || ''}
              onChange={(e) => handleStepConfigChange(index, 'email_body', e.target.value)}
            />
          </Stack>
        );

      case 'SEND_NOTIFICATION':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={config.title || ''}
              onChange={(e) => handleStepConfigChange(index, 'title', e.target.value)}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={3}
              value={config.message || ''}
              onChange={(e) => handleStepConfigChange(index, 'message', e.target.value)}
            />
          </Stack>
        );

      case 'WAIT_DELAY':
        return (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delay (days)"
                type="number"
                fullWidth
                value={config.delay_days || 0}
                onChange={(e) => handleStepConfigChange(index, 'delay_days', parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Delay (hours)"
                type="number"
                fullWidth
                value={config.delay_hours || 0}
                onChange={(e) => handleStepConfigChange(index, 'delay_hours', parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        );

      case 'CHECK_CONDITION':
        return (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Field</InputLabel>
                <Select
                  value={config.field || 'contact.balance_due'}
                  label="Field"
                  onChange={(e) => handleStepConfigChange(index, 'field', e.target.value)}
                >
                  {CONDITION_FIELDS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={config.operator || 'gt'}
                  label="Operator"
                  onChange={(e) => handleStepConfigChange(index, 'operator', e.target.value)}
                >
                  {CONDITION_OPERATORS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Value"
                fullWidth
                value={config.value || ''}
                onChange={(e) => handleStepConfigChange(index, 'value', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 'REQUIRE_APPROVAL':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={3}
              value={config.message || ''}
              onChange={(e) => handleStepConfigChange(index, 'message', e.target.value)}
            />
          </Stack>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 }, maxWidth: 900, mx: 'auto', fontFamily }}>
      {/* Top Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ fontFamily, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {isEditing ? 'Edit Workflow' : 'Create Workflow'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows`)}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Template Selector */}
      {!isEditing && showTemplates && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TemplateIcon color="primary" />
              <Typography variant="h6" fontWeight={600} sx={{ fontFamily }}>
                Start from a Template
              </Typography>
            </Box>
            <Button size="small" onClick={() => setShowTemplates(false)}>
              Start from Scratch
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily }}>
            Choose a pre-built template to get started quickly. You can customize everything after applying.
          </Typography>
          <Grid container spacing={2}>
            {WORKFLOW_TEMPLATES.map((tpl) => (
              <Grid item xs={12} sm={6} md={4} key={tpl.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: '0.2s',
                    borderColor: 'divider',
                    '&:hover': { borderColor: tpl.color, boxShadow: 2, transform: 'translateY(-2px)' }
                  }}
                  onClick={() => applyTemplate(tpl)}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3 }}>
                    <Box sx={{ mb: 1.5 }}>{tpl.icon}</Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily, mb: 0.5 }}>
                      {tpl.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                      {tpl.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Chip
                      label={`${tpl.steps.length} steps`}
                      size="small"
                      sx={{ bgcolor: `${tpl.color}15`, color: tpl.color, fontWeight: 600 }}
                    />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {!isEditing && !showTemplates && (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<TemplateIcon />}
            onClick={() => setShowTemplates(true)}
            sx={{ fontFamily }}
          >
            Choose from Templates
          </Button>
        </Box>
      )}

      {/* Basic Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily }}>
          Basic Info
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          <TextField
            label="Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Workflow name"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Workflow description..."
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Workflow Type</InputLabel>
                <Select
                  value={form.workflow_type}
                  label="Workflow Type"
                  onChange={(e) => handleChange('workflow_type', e.target.value)}
                >
                  {WorkflowTypeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={form.trigger_type}
                  label="Trigger Type"
                  onChange={(e) => {
                    handleChange('trigger_type', e.target.value);
                    handleChange('trigger_config', {});
                  }}
                >
                  {WorkflowTriggerTypeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
            }
            label="Active"
          />
        </Stack>
      </Paper>

      {/* Trigger Config */}
      {renderTriggerConfig()}

      {/* Steps Builder */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontFamily }}>
          Workflow Steps
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {steps.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No steps added yet. Use the controls below to add workflow steps.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {steps.map((step, index) => (
              <Paper
                key={step.id}
                variant="outlined"
                sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}
              >
                {/* Step Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DragIndicatorIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                  <Chip
                    label={`Step ${index + 1}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                    {STEP_ICON_MAP[step.step_type]}
                  </Box>
                  <Chip
                    label={WorkflowStepTypeLabels[step.step_type] || step.step_type}
                    size="small"
                    color="default"
                  />
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Step name"
                    value={step.name}
                    onChange={(e) => handleStepNameChange(index, e.target.value)}
                    sx={{ flex: 1, ml: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveStep(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveStep(index, 1)}
                      disabled={index === steps.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveStep(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Step Config */}
                {renderStepConfig(step, index)}
              </Paper>
            ))}
          </Stack>
        )}

        {/* Add Step */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Step Type</InputLabel>
            <Select
              value={addStepType}
              label="Step Type"
              onChange={(e) => setAddStepType(e.target.value)}
            >
              {WorkflowStepTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {STEP_ICON_MAP[opt.value]}
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddStep}
            disabled={!addStepType}
          >
            Add Step
          </Button>
        </Box>
      </Paper>

      {/* Bottom Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows`)}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default WorkflowForm;
